/**
 * Share code validation and target-path building for `/s/:code`
 * (web/docs/sharing-plan.md §4, §5.3). Mirrors the mobile
 * `app/src/utils/share-route.ts` validation — a web-local port since `app/`
 * is off-limits to import from (web/docs/data-layer.md §1). Kept separate
 * from the screen so the target route is never built from an unvalidated
 * payload value.
 */

/** The backend mints 8 base62 characters (docs/sharing-plan.md §3, §11.1). */
const CODE_RE = /^[A-Za-z0-9]{8}$/;
const TMDB_ID_RE = /^\d{1,16}$/;

export function isValidShareCode(code: string | undefined | null): code is string {
  return typeof code === 'string' && CODE_RE.test(code);
}

/**
 * The target path for a resolved share: `/show/:id` for both shows and
 * movies (movies add `type=movie` — TMDB ids overlap, lib/title-path), with
 * the share code attached as `?s=` for the attribution chip.
 * Re-validates `titleType`/`tmdbId` independently of whatever shape the
 * caller already trusts — returns `null` rather than build a path from an
 * unexpected value.
 */
export function shareTargetPath(titleType: string, tmdbId: string, code: string): string | null {
  if (titleType !== 'show' && titleType !== 'movie') return null;
  if (!TMDB_ID_RE.test(tmdbId)) return null;
  if (!isValidShareCode(code)) return null;
  return titleType === 'movie' ? `/show/${tmdbId}?type=movie&s=${code}` : `/show/${tmdbId}?s=${code}`;
}

/** The custom-scheme deep link a preview page's "Open in app" button uses. */
export function appSchemeLink(code: string): string {
  return `app.screenatlas://s/${code}`;
}
